import { PLAN_STATE, type PlanState } from "@next-platform/client-common-core";
import { IconVariants } from "@next-platform/client-common-spicy-ui";
import type { TFunction } from "i18next";

type ResolvePeriodLabelParams = {
  planState: PlanState;
  hasExpiryDate: boolean;
  formattedExpiryDate: string;
  daysRemaining: number;
  t: TFunction;
  includeDaysCount?: boolean;
  hasRenewsDate?: boolean;
  formattedRenewsDate?: string;
};

export const PLAN_STATE_STATUS: Record<PlanState, IconVariants | null> = {
  [PLAN_STATE.FREE]: null,
  [PLAN_STATE.ONE_TIME_ACTIVE]: IconVariants.CLOCK,
  [PLAN_STATE.RECURRING_ACTIVE]: IconVariants.REFRESH,
  [PLAN_STATE.GRACE]: IconVariants.CREDIT_CARD,
  [PLAN_STATE.CANCELLED]: IconVariants.BAN,
  [PLAN_STATE.PAUSED]: IconVariants.BAN,
  [PLAN_STATE.EXPIRED]: IconVariants.CLOSE_CIRCLE,
};

export const resolvePeriodLabel = ({
  planState,
  hasExpiryDate,
  formattedExpiryDate,
  daysRemaining,
  t,
  includeDaysCount = true,
  hasRenewsDate = false,
  formattedRenewsDate = "",
}: ResolvePeriodLabelParams): string => {
  if (planState === PLAN_STATE.GRACE) {
    return t("user:profile.currentPlan.periodLabel.graceActive");
  }

  if (planState === PLAN_STATE.RECURRING_ACTIVE) {
    if (!hasRenewsDate || !formattedRenewsDate) {
      return "";
    }

    return t("user:profile.currentPlan.periodLabel.renews", { date: formattedRenewsDate });
  }

  if (planState === PLAN_STATE.FREE || !hasExpiryDate || !formattedExpiryDate) {
    return "";
  }

  const periodLabelsByState = {
    [PLAN_STATE.ONE_TIME_ACTIVE]: includeDaysCount
      ? t("user:profile.currentPlan.periodLabel.expires", {
          date: formattedExpiryDate,
          count: daysRemaining,
        })
      : t("subscription:overview.periodLabel.expires", { date: formattedExpiryDate }),
    [PLAN_STATE.CANCELLED]: includeDaysCount
      ? t("user:profile.currentPlan.periodLabel.accessEnds", {
          date: formattedExpiryDate,
          count: daysRemaining,
        })
      : t("subscription:overview.periodLabel.accessEnds", { date: formattedExpiryDate }),
    [PLAN_STATE.PAUSED]: includeDaysCount
      ? t("user:profile.currentPlan.periodLabel.accessEnds", {
          date: formattedExpiryDate,
          count: daysRemaining,
        })
      : t("subscription:overview.periodLabel.accessEnds", { date: formattedExpiryDate }),
    [PLAN_STATE.EXPIRED]: includeDaysCount
      ? t("user:profile.currentPlan.periodLabel.ended", { date: formattedExpiryDate })
      : t("subscription:overview.expiredOn", { date: formattedExpiryDate }),
  } as const;

  return periodLabelsByState[planState] ?? "";
};

export const resolveStatusLabel = ({ planState, t }: { planState: PlanState; t: TFunction }): string => {
  const statusLabels = {
    [PLAN_STATE.FREE]: t("user:profile.currentPlan.statusLabel.noActiveSubscription"),
    [PLAN_STATE.ONE_TIME_ACTIVE]: t("user:profile.currentPlan.statusLabel.oneTime"),
    [PLAN_STATE.RECURRING_ACTIVE]: t("user:profile.currentPlan.statusLabel.autoRenews"),
    [PLAN_STATE.GRACE]: t("user:profile.currentPlan.statusLabel.paymentFailed"),
    [PLAN_STATE.CANCELLED]: t("user:profile.currentPlan.statusLabel.cancelled"),
    [PLAN_STATE.PAUSED]: t("user:profile.currentPlan.statusLabel.paused"),
    [PLAN_STATE.EXPIRED]: t("user:profile.currentPlan.statusLabel.expired"),
  } as const;

  return statusLabels[planState];
};
